import { useWindowResize } from '@helpers';
import {
  AnalyticsLineChart,
  AnalyticsLineChartProps,
} from '@terminal/modules/analytics-line-chart/AnalyticsLineChart';
import { useCatalystInsightsList } from '@terminal/react-query-hooks/insights/useCatalystInsights';
import { Horizon } from '@terminal/views/theme-overview/theme-overview-drawer/components/theme-performance-section/ThemePerformanceSection';
import { useMemo, useRef } from 'react';

import { InsightsOverlay } from '../insights-overlay/InsightsOverlay';
import styles from './AnalyticsLineChartArea.module.scss';

const CATALYST_HORIZONS: Horizon[] = ['1D', '1W', '1M', '3M'];

export interface AnalyticsLineChartWithInsightsProps
  extends AnalyticsLineChartProps {
  themes: string[];
  selectedHorizon: Horizon;
}

export const AnalyticsLineChartArea = ({
  themes,
  multiLineConfig,
  selectedHorizon,
  ...chartProps
}: AnalyticsLineChartWithInsightsProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { width: chartWidth } = useWindowResize({ targetRef: containerRef });

  const { minDate, maxDate } = useMemo(() => {
    const aggregateData = multiLineConfig.aggregate?.[0]?.data ?? [];

    return {
      minDate: aggregateData[0]?.date,
      maxDate: aggregateData.at(-1)?.date,
    };
  }, [multiLineConfig]);

  const isHorizonWithinCatalystRange =
    CATALYST_HORIZONS.includes(selectedHorizon);

  const { data: catalystInsights, isLoading } = useCatalystInsightsList(
    {
      themes,
      from: minDate?.toISOString(),
      to: maxDate?.toISOString(),
    },
    isHorizonWithinCatalystRange
  );

  const shouldShowInsights =
    !isLoading && !!catalystInsights?.length && !!minDate && !!maxDate;

  return (
    <div className={styles.chartContainer} ref={containerRef}>
      <AnalyticsLineChart multiLineConfig={multiLineConfig} {...chartProps} />
      {shouldShowInsights && (
        <InsightsOverlay
          insights={catalystInsights}
          chartWidth={chartWidth}
          minDate={minDate}
          maxDate={maxDate}
        />
      )}
    </div>
  );
};
